// Pure draft/build/validate helpers for marker authoring (UI Layering Sweep
// Phase 2 split of MarkerAuthoringForm.tsx — markers.md Module plan). No React:
// everything here maps a revision to an editable draft and a draft back to the
// typed /v1 request bodies, returning a human validation message (string)
// instead of a body when the draft is invalid. Behavior is byte-for-byte the
// pre-split MarkerAuthoringForm logic.

import type { components } from "../api/schema";

export type Assertion = components["schemas"]["Assertion"];
export type AuthoringEvaluator = "rule" | "judge";
export type JudgeModelOption =
  components["schemas"]["JudgeModelOptionResponse"];
export type JudgeModelPolicy =
  components["schemas"]["JudgeModelPolicyResponse"];
export type Locator = NonNullable<
  components["schemas"]["MarkerCreate"]["locator"]
>;
export type MarkerAutomate = components["schemas"]["MarkerAutomate"];
export type MarkerCreate = components["schemas"]["MarkerCreate"];
export type MarkerOutput = components["schemas"]["MarkerOutput"];
// The typed output constraint (`output_spec`): how a boolean is worded, the
// numeric range, or the allowed category set. The union is inlined by the
// generator, so it is named here off the request body.
export type MarkerOutputSpec = NonNullable<MarkerCreate["output_spec"]>;
export type MarkerRevise = components["schemas"]["MarkerRevise"];
export type MarkerRevisionResponse =
  components["schemas"]["MarkerRevisionResponse"];
export type ReviewPolicy = components["schemas"]["ReviewPolicy"];
export type Verdict = components["schemas"]["Verdict"];

export type RuleKind = "string" | "regex";
export type AssertionMode = "value" | "verdict";
export type ConditionMatch =
  | "=="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "equals"
  | "in"
  | "not_in";

export interface LocatorDraft {
  endCharExclusive: string;
  endIndexExclusive: string;
  endMsExclusive: string;
  kind: Locator["kind"];
  nodeId: string;
  startChar: string;
  startIndex: string;
  startMs: string;
  utteranceIndex: string;
}

export interface AssertionCaseDraft {
  id: number;
  match: ConditionMatch;
  value: string;
  verdict: Verdict;
}

export interface DefinitionDraft {
  assertionCases: AssertionCaseDraft[];
  assertionMode: AssertionMode;
  categoryValues: string[];
  defaultVerdict: Verdict;
  displayName: string;
  evaluator: AuthoringEvaluator | "";
  judgeModelRef: string;
  judgeRubric: string;
  locator: LocatorDraft;
  numberMax: string;
  numberMin: string;
  output: MarkerOutput | "";
  description: string;
  reviewPolicy: ReviewPolicy;
  reviewSampleRate: string;
  ruleKind: RuleKind;
  ruleValue: string;
}

export const blankLocator: LocatorDraft = {
  endCharExclusive: "",
  endIndexExclusive: "",
  endMsExclusive: "",
  kind: "whole_transcript",
  nodeId: "",
  startChar: "",
  startIndex: "",
  startMs: "",
  utteranceIndex: "",
};

export function newCase(
  output: MarkerOutput | "",
  id: number,
): AssertionCaseDraft {
  return {
    id,
    match: output === "number" ? ">=" : "equals",
    value: output === "boolean" ? "true" : "",
    verdict: "pass",
  };
}

function locatorToDraft(locator: Locator | undefined): LocatorDraft {
  if (locator == null || locator.kind === "whole_transcript") {
    return { ...blankLocator };
  }
  if (locator.kind === "audio_span") {
    return {
      ...blankLocator,
      endMsExclusive: String(locator.end_ms_exclusive),
      kind: locator.kind,
      startMs: String(locator.start_ms),
    };
  }
  if (locator.kind === "utterance_range") {
    return {
      ...blankLocator,
      endIndexExclusive: String(locator.end_index_exclusive),
      kind: locator.kind,
      startIndex: String(locator.start_index),
    };
  }
  if (locator.kind === "char_span") {
    return {
      ...blankLocator,
      endCharExclusive: String(locator.end_char_exclusive),
      kind: locator.kind,
      startChar: String(locator.start_char),
      utteranceIndex:
        locator.utterance_index == null ? "" : String(locator.utterance_index),
    };
  }
  return {
    ...blankLocator,
    kind: locator.kind,
    nodeId: locator.node_id,
  };
}

function assertionCasesToDraft(
  assertion: Assertion | null | undefined,
): AssertionCaseDraft[] {
  return (assertion?.cases ?? []).map((item, index) => {
    const when = item.when;
    if ("operator" in when) {
      return {
        id: index,
        match: when.operator,
        value: String(when.value),
        verdict: item.then,
      };
    }
    if ("in" in when) {
      return {
        id: index,
        match: "in" as const,
        value: when.in.join(", "),
        verdict: item.then,
      };
    }
    if ("not_in" in when) {
      return {
        id: index,
        match: "not_in" as const,
        value: when.not_in.join(", "),
        verdict: item.then,
      };
    }
    return {
      id: index,
      match: "equals" as const,
      value: String(when.equals),
      verdict: item.then,
    };
  });
}

export function definitionSeed(
  revision: MarkerRevisionResponse | undefined,
): DefinitionDraft {
  const spec = revision?.spec;
  const judgeSpec = spec != null && "model_ref" in spec ? spec : null;
  const regexSpec = spec != null && "regex" in spec ? spec : null;
  const stringSpec = spec != null && "string_pattern" in spec ? spec : null;
  const output = revision?.output ?? "";
  const cases = assertionCasesToDraft(revision?.assertion);
  const outputSpec = revision?.output_spec ?? null;
  return {
    assertionCases: cases.length > 0 ? cases : [newCase(output, 0)],
    assertionMode: revision?.assertion == null ? "value" : "verdict",
    categoryValues: outputSpec?.kind === "category" ? outputSpec.values : [],
    defaultVerdict: revision?.assertion?.default ?? "fail",
    displayName: revision?.display_name ?? "",
    evaluator:
      revision?.evaluator === "rule" || revision?.evaluator === "judge"
        ? revision.evaluator
        : "",
    judgeModelRef: judgeSpec?.model_ref ?? "",
    judgeRubric: judgeSpec?.rubric_pattern ?? "",
    locator: locatorToDraft(revision?.locator),
    numberMax: outputSpec?.kind === "number" ? String(outputSpec.max) : "",
    numberMin: outputSpec?.kind === "number" ? String(outputSpec.min) : "",
    output,
    description: revision?.description ?? "",
    reviewPolicy: revision?.review_policy ?? "never",
    reviewSampleRate:
      revision?.review_sample_rate == null
        ? ""
        : String(revision.review_sample_rate),
    ruleKind: regexSpec == null ? "string" : "regex",
    ruleValue: regexSpec?.regex ?? stringSpec?.string_pattern ?? "",
  };
}

function parseInteger(
  value: string,
  label: string,
  required: boolean,
): number | null | string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return required ? `${label} is required.` : null;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    return `${label} must be a non-negative integer.`;
  }
  return parsed;
}

export function buildLocator(draft: LocatorDraft): Locator | string {
  if (draft.kind === "whole_transcript") {
    return { kind: "whole_transcript" };
  }
  if (draft.kind === "audio_span") {
    const start = parseInteger(draft.startMs, "Start time", true);
    const end = parseInteger(draft.endMsExclusive, "End time", true);
    if (typeof start === "string" || typeof end === "string") {
      return typeof start === "string" ? start : (end as string);
    }
    if (start == null || end == null || end <= start) {
      return "End time must be after start time.";
    }
    return { end_ms_exclusive: end, kind: "audio_span", start_ms: start };
  }
  if (draft.kind === "utterance_range") {
    const start = parseInteger(draft.startIndex, "First utterance", true);
    const end = parseInteger(draft.endIndexExclusive, "End utterance", true);
    if (typeof start === "string" || typeof end === "string") {
      return typeof start === "string" ? start : (end as string);
    }
    if (start == null || end == null || end <= start) {
      return "End utterance must be after the first utterance.";
    }
    return {
      end_index_exclusive: end,
      kind: "utterance_range",
      start_index: start,
    };
  }
  if (draft.kind === "char_span") {
    const utterance = parseInteger(
      draft.utteranceIndex,
      "Utterance index",
      false,
    );
    const start = parseInteger(draft.startChar, "First character", true);
    const end = parseInteger(draft.endCharExclusive, "End character", true);
    if (typeof utterance === "string") {
      return utterance;
    }
    if (typeof start === "string") {
      return start;
    }
    if (typeof end === "string") {
      return end;
    }
    if (start == null || end == null || end <= start) {
      return "End character must be after the first character.";
    }
    return {
      end_char_exclusive: end,
      kind: "char_span",
      start_char: start,
      utterance_index: utterance,
    };
  }
  const nodeId = draft.nodeId.trim();
  return nodeId
    ? { kind: "trace_node", node_id: nodeId }
    : "Trace node is required.";
}

function categoryValues(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// The declared category set, when the draft has one. Shared by the
// condition controls so an `equals` condition can pick from the declared
// values instead of retyping them.
export function declaredCategories(draft: DefinitionDraft): string[] {
  return draft.categoryValues;
}

// A boolean is always true/false. A rule marker derives its number (match
// count) or category (matched text) from the match itself, so a range or
// category set only constrains a judge (or a not-yet-chosen method).
export function outputSpecApplies(draft: DefinitionDraft): boolean {
  return draft.output !== "boolean" && draft.evaluator !== "rule";
}

export function buildOutputSpec(
  draft: DefinitionDraft,
): MarkerOutputSpec | null | string {
  if (draft.output === "" || !outputSpecApplies(draft)) {
    return null;
  }
  if (draft.output === "number") {
    const min = Number(draft.numberMin.trim());
    const max = Number(draft.numberMax.trim());
    if (
      draft.numberMin.trim().length === 0 ||
      draft.numberMax.trim().length === 0 ||
      !Number.isFinite(min) ||
      !Number.isFinite(max)
    ) {
      return "Number markers need a minimum and a maximum.";
    }
    if (min >= max) {
      return "The maximum must be greater than the minimum.";
    }
    return { kind: "number", max, min };
  }
  const values = draft.categoryValues;
  if (values.length === 0) {
    return "Category markers need at least one category.";
  }
  return { kind: "category", values };
}

export function buildAssertion(
  draft: DefinitionDraft,
): Assertion | null | string {
  if (draft.assertionMode === "value") {
    return null;
  }
  if (draft.output === "") {
    return "Output type is required.";
  }
  if (draft.assertionCases.length === 0) {
    return "At least one result condition is required.";
  }

  const cases: Assertion["cases"] = [];
  for (const item of draft.assertionCases) {
    if (draft.output === "number") {
      const rawValue = item.value.trim();
      if (rawValue.length === 0) {
        return "Every number condition requires a numeric value.";
      }
      const value = Number(rawValue);
      if (!Number.isFinite(value)) {
        return "Every number condition requires a numeric value.";
      }
      const comparisonOperator = item.match;
      if (!["==", "!=", "<", "<=", ">", ">="].includes(comparisonOperator)) {
        return "Every number condition requires a comparison.";
      }
      cases.push({
        then: item.verdict,
        when: {
          operator: comparisonOperator as "==" | "!=" | "<" | "<=" | ">" | ">=",
          value,
        },
      });
      continue;
    }
    if (draft.output === "boolean") {
      if (item.value !== "true" && item.value !== "false") {
        return "Every boolean condition requires true or false.";
      }
      cases.push({
        then: item.verdict,
        when: { equals: item.value === "true" },
      });
      continue;
    }

    const values = categoryValues(item.value);
    if (values.length === 0) {
      return "Every category condition requires a value.";
    }
    if (item.match === "in") {
      cases.push({ then: item.verdict, when: { in: values } });
    } else if (item.match === "not_in") {
      cases.push({ then: item.verdict, when: { not_in: values } });
    } else {
      if (values.length !== 1) {
        return "An equals condition accepts one category.";
      }
      cases.push({ then: item.verdict, when: { equals: values[0] ?? "" } });
    }
  }

  return { cases, default: draft.defaultVerdict };
}

export function buildSpec(
  draft: DefinitionDraft,
): MarkerCreate["spec"] | null | string {
  if (draft.evaluator === "") {
    return null;
  }
  if (draft.evaluator === "rule") {
    const value = draft.ruleValue;
    if (value.length === 0) {
      return draft.ruleKind === "regex"
        ? "Regular expression is required."
        : "Text to find is required.";
    }
    return draft.ruleKind === "regex"
      ? { regex: value }
      : { string_pattern: value };
  }
  const rubric = draft.judgeRubric.trim();
  const modelRef = draft.judgeModelRef.trim();
  if (rubric.length === 0) {
    return "Rubric is required.";
  }
  if (modelRef.length === 0) {
    return "Model reference is required.";
  }
  return { model_ref: modelRef, rubric_pattern: rubric };
}

// Judge model-policy validation: hosted installs restrict the judge model_ref
// to the server-published catalog; free-text remains for self-managed BYO
// installs.
export function judgeModelError(
  draft: DefinitionDraft,
  policy: JudgeModelPolicy,
): string | null {
  if (draft.evaluator !== "judge" || policy.allows_free_text) {
    return null;
  }
  return policy.model_refs.includes(draft.judgeModelRef.trim())
    ? null
    : "Select an available judge model.";
}

// #1302: judge_model_policy widens with per-option display metadata
// (docs/04 §11). Build a model_ref -> option lookup once per render.
export function judgeModelOptionsByRef(
  policy: JudgeModelPolicy,
): Map<string, JudgeModelOption> {
  return new Map(
    policy.model_options.map((option) => [option.model_ref, option]),
  );
}

// Human-readable picker label: display_name when present (falling back to
// the bare model_ref, e.g. a self-managed install with no richer metadata),
// with the operator/vendor-declared context_window appended when informative.
export function judgeModelOptionLabel(
  modelRef: string,
  option: JudgeModelOption | undefined,
): string {
  const label = option?.display_name ?? modelRef;
  const contextWindow = option?.context_window;
  if (contextWindow == null) {
    return label;
  }
  const formatted =
    contextWindow >= 1000
      ? `${Math.round(contextWindow / 1000).toString()}K`
      : contextWindow.toString();
  return `${label} (${formatted} context)`;
}

export function buildRevisionBody(
  draft: DefinitionDraft,
): MarkerRevise | string {
  const description = draft.description.trim() || null;
  const displayName = draft.displayName.trim();
  if (displayName.length === 0) {
    return "Display name is required.";
  }
  if (draft.output === "") {
    return "Output type is required.";
  }
  const locator = buildLocator(draft.locator);
  if (typeof locator === "string") {
    return locator;
  }
  const spec = buildSpec(draft);
  if (typeof spec === "string") {
    return spec;
  }
  const outputSpec = buildOutputSpec(draft);
  if (typeof outputSpec === "string") {
    return outputSpec;
  }
  const assertion = buildAssertion(draft);
  if (typeof assertion === "string") {
    return assertion;
  }
  let sampleRate: number | null = null;
  if (draft.reviewPolicy === "sample") {
    sampleRate = Number(draft.reviewSampleRate.trim());
    if (
      !Number.isSafeInteger(sampleRate) ||
      sampleRate < 1 ||
      sampleRate > 100
    ) {
      return "Review sample must be an integer from 1 to 100.";
    }
  }
  return {
    assertion,
    display_name: displayName,
    evaluator: draft.evaluator === "" ? null : draft.evaluator,
    locator,
    output: draft.output,
    output_spec: outputSpec,
    description,
    review_policy: draft.reviewPolicy,
    review_sample_rate: sampleRate,
    spec,
  };
}

export function buildAutomateBody(
  draft: DefinitionDraft,
): MarkerAutomate | string {
  if (draft.evaluator === "") {
    return "Evaluation method is required.";
  }
  const spec = buildSpec(draft);
  if (typeof spec === "string" || spec == null) {
    return typeof spec === "string"
      ? spec
      : "Evaluation settings are required.";
  }
  const assertion = buildAssertion(draft);
  if (typeof assertion === "string") {
    return assertion;
  }
  return { assertion, evaluator: draft.evaluator, spec };
}
